import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const intervalRef = useRef(null);

  // Load all images from folder main_00001
  const images = useMemo(() => {
    try {
      const context = require.context(
        "../../common/assets/images/main_00001",
        false,
        /\.(png|jpe?g|svg)$/
      );
      return context.keys().map((key) => context(key));
    } catch (e) {
      return [];
    }
  }, []);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) =>
      prev > 0 ? prev - 1 : Math.max(images.length - 1, 0)
    );
    resetAutoPlay();
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    resetAutoPlay();
  };

  const handleSlideClick = (index) => {
    if (index !== currentSlide) {
      setCurrentSlide(index);
      resetAutoPlay();
    }
  };

  // Auto-play slider
  useEffect(() => {
    if (images.length <= 1) return; // Không cần auto-play nếu chỉ có 1 ảnh

    // Tự động chuyển slide sau 3 giây
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    }, 3000);

    // Cleanup interval khi component unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [images.length]);

  // Reset interval khi user tương tác với slider
  const resetAutoPlay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (images.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev < images.length - 1 ? prev + 1 : 0));
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9dfe8]">
      {/* Hero Section */}
      <section className="max-w-7xl 3xl:max-w-[1600px] mx-auto px-4 sm:px-6 3xl:px-12 py-8 sm:py-12 md:py-16 3xl:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 3xl:gap-20 items-center">
          {/* Left Content */}
          <div className="flex flex-col items-center sm:items-start text-center sm:text-left">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl 3xl:text-7xl font-bold mb-6 sm:mb-8 md:mb-8 3xl:mb-12 leading-tight">
              <span className="text-gray-800 mb-2 block">
                KHÓA HỌC TOÀN DIỆN
              </span>
              <span className="text-gray-800 mb-2 block">HƯỚNG TỚI KỲ THI</span>
              <span className="text-[#db3b6d] block">ĐÁNH GIÁ TƯ DUY 2026</span>
            </h1>

            {/* Course Features */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-3 md:gap-4 3xl:gap-6 mb-6 sm:mb-8 3xl:mb-12 items-center sm:items-start text-center sm:text-left">
              {/* Box 1 */}
              <div className="flex flex-col max-w-[200px] sm:max-w-none">
                <div className="bg-[#4b4b4b] text-white py-2.5 sm:py-2.5 3xl:py-3.5 px-3 sm:px-4 rounded-lg font-bold mb-2 sm:mb-3 3xl:mb-5 text-sm sm:text-sm md:text-base 3xl:text-xl text-center">
                  30 ĐỀ TSA
                </div>
                <p className="text-sm sm:text-sm 3xl:text-lg text-gray-700 text-center sm:text-left">
                  Khóa học gồm 30 đề thi thử bám sát cấu trúc kỳ thi vừa đảm bảo
                  hay, lạ, khó
                </p>
              </div>

              {/* Box 2 */}
              <div className="flex flex-col max-w-[200px] sm:max-w-none">
                <div className="bg-[#4b4b4b] text-white py-2.5 sm:py-2.5 3xl:py-3.5 px-3 sm:px-4 rounded-lg font-bold mb-2 sm:mb-3 3xl:mb-5 text-sm sm:text-sm md:text-base 3xl:text-xl text-center">
                  25 + BÀI GIẢNG
                </div>
                <p className="text-sm sm:text-sm 3xl:text-lg text-gray-700 text-center sm:text-left">
                  Khóa học gồm hơn 25 bài giảng gồm các chủ đề kiến thức từ lớp
                  10 tới lớp 12
                </p>
              </div>

              {/* Box 3 */}
              <div className="flex flex-col max-w-[200px] sm:max-w-none">
                <div className="bg-[#4b4b4b] text-white py-2.5 sm:py-2.5 3xl:py-3.5 px-3 sm:px-4 rounded-lg font-bold mb-2 sm:mb-3 3xl:mb-5 text-sm sm:text-sm md:text-base 3xl:text-xl text-center">
                  TÀI LIỆU
                </div>
                <p className="text-sm sm:text-sm 3xl:text-lg text-gray-700 text-center sm:text-left">
                  Tài liệu trong khóa học được biên soạn bằng Latex đảm bảo
                  chuyên nghiệp
                </p>
              </div>
            </div>

            {/* CTA Button */}
            <Link
              to="https://drive.google.com/file/d/1dyBoNnKWPky6yy4WN4sA5Dpl2jS0ZZyE/view?usp=sharing"
              target="_blank"
              className="inline-block bg-[#db3b6d] text-white px-6 sm:px-8 md:px-8 3xl:px-12 py-3 sm:py-3 3xl:py-5 rounded-lg font-bold text-base sm:text-base md:text-lg 3xl:text-2xl hover:bg-[#a92e55] transition-colors shadow-lg text-center w-full sm:w-auto"
            >
              XEM LỘ TRÌNH HỌC
            </Link>
          </div>

          {/* Right Content - Slider */}
          <div className="relative mt-8 lg:mt-0">
            <div className="rounded-lg p-4 sm:p-6 3xl:p-10">
              <h3 className="text-center text-gray-700 font-bold mb-2 sm:mb-[-12px] 3xl:mb-10 text-sm sm:text-base md:text-lg 3xl:text-2xl">
                <span className="inline-block px-2 sm:px-3 md:px-6 3xl:px-10 py-1.5 sm:py-2 3xl:py-4 rounded-md border border-gray-400 shadow-sm text-xs sm:text-sm md:text-base 3xl:text-xl break-words sm:whitespace-nowrap">
                  MỘT SỐ HÌNH ẢNH TỪ KHÓA HỌC
                </span>
              </h3>
              {/* Slider */}
              <div className="relative h-[240px] md:h-[370px] lg:h-[420px] 3xl:h-[550px] flex items-center justify-center overflow-hidden rounded-lg">
                {images.length > 0 ? (
                  <div className="relative w-full h-full flex items-center justify-center">
                    {images.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`slide-${index}`}
                        className={`absolute object-contain rounded min-w-[27%] md:min-w-[32%] lg:min-w-[60%] 3xl:min-w-[70%] transition-all duration-700 ease-in-out ${
                          index === currentSlide
                            ? "opacity-100 scale-100 translate-x-0 z-10"
                            : "opacity-0 scale-95 translate-x-4 z-0 pointer-events-none"
                        }`}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-500 3xl:text-xl">Chưa có ảnh</div>
                )}
                {/* Navigation Arrows */}
                <button
                  onClick={handlePrevSlide}
                  className="absolute left-2 sm:left-4 3xl:left-8 top-1/2 -translate-y-1/2 bg-[#f1c0cf] hover:bg-[#db3b6d] text-white w-6 h-12 sm:w-7 sm:h-[3.5rem] md:w-8 md:h-[3.5rem] 3xl:w-10 3xl:h-24 rounded-lg transition-colors flex items-center justify-center shadow z-20"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 3xl:w-7 3xl:h-7 text-white stroke-[3]" />
                </button>
                <button
                  onClick={handleNextSlide}
                  className="absolute right-2 sm:right-4 3xl:right-8 top-1/2 -translate-y-1/2 bg-[#f1c0cf] hover:bg-[#db3b6d] text-white w-6 h-12 sm:w-7 sm:h-[3.5rem] md:w-8 md:h-[3.5rem] 3xl:w-10 3xl:h-24 rounded-lg transition-colors flex items-center justify-center shadow z-20"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 3xl:w-7 3xl:h-7 text-white stroke-[3]" />
                </button>
              </div>

              {/* Slide Indicators */}
              <div className="flex justify-center space-x-2 sm:space-x-3 3xl:space-x-5 mt-4 sm:mt-6 3xl:mt-10">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleSlideClick(index)}
                    className={`w-2.5 h-2.5 sm:w-3 sm:h-3 3xl:w-4 3xl:h-4 rounded-full transition-colors ${
                      currentSlide === index
                        ? "bg-[#db3b6d]"
                        : "bg-transparent border-2 border-gray-400"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
